import { matchPath } from 'react-router-dom'

const invalidRedirectValues = new Set(['', 'undefined', 'null'])

interface IShouldForceRedirectToNewAppInput {
  isWebsiteRoute: boolean
  newAppUrl: string | null | undefined
}

interface IResolveNewAppRedirectPathInput {
  newAppUrl: string | null | undefined
  pathname: string
  selectedWorkspaceName?: string | null
}

interface IRedirectMatchContext {
  params: Record<string, string | undefined>
  selectedWorkspaceName?: string | null
}

interface IRedirectRoute {
  getRedirectPath: (context: IRedirectMatchContext) => string | null
  path: string
}

const normalizePathname = (pathname: string) => {
  if (pathname === '/') return pathname
  const normalizedPath = pathname.replace(/\/+$/, '')
  return normalizedPath || '/'
}

const resolveWorkspaceName = (params: Record<string, string | undefined>, selectedWorkspaceName?: string | null) => {
  return params.workspace_name || params.workspaceName || params.handle || selectedWorkspaceName || null
}

const getWorkspacePath = (
  params: Record<string, string | undefined>,
  selectedWorkspaceName: string | null | undefined,
  suffix: string
) => {
  const workspaceName = resolveWorkspaceName(params, selectedWorkspaceName)
  if (!workspaceName) return null
  return `/${workspaceName}${suffix}`
}

const redirectRoutes: IRedirectRoute[] = [
  { getRedirectPath: () => '/user/shipments', path: '/assigned-to-me' },
  {
    getRedirectPath: ({ params }) => `/user/shipments/${params.shipment_id}`,
    path: '/assigned-to-me/shipments/:shipment_id'
  },
  { getRedirectPath: () => '/user/inbox/direct-messages', path: '/direct-messages' },
  { getRedirectPath: () => '/user/inbox/shipment-chats', path: '/shipment-chats' },
  { getRedirectPath: () => '/user/inbox', path: '/offer-chats' },
  { getRedirectPath: () => '/user/inbox/team-member-chats', path: '/team-member/:member_id' },
  { getRedirectPath: () => '/user/inbox', path: '/inbox' },
  { getRedirectPath: () => '/user/offers', path: '/offers' },
  { getRedirectPath: () => '/user/posts', path: '/my-posts' },
  { getRedirectPath: () => '/user/settings/profile', path: '/account/profile' },
  { getRedirectPath: () => '/user/settings/workspaces', path: '/account/workspaces' },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/shipments'),
    path: '/:workspace_name/shipments'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, `/shipments/${params.shipment_id}`),
    path: '/:workspace_name/shipments/:shipment_id'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/containers'),
    path: '/:workspace_name/container-tracking'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) => getWorkspacePath(params, selectedWorkspaceName, '/posts'),
    path: '/:workspace_name/posts'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) => getWorkspacePath(params, selectedWorkspaceName, '/offers'),
    path: '/:workspace_name/offers'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) => getWorkspacePath(params, selectedWorkspaceName, '/network'),
    path: '/:workspace_name/network'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/settings'),
    path: '/:workspace_name/account'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/settings'),
    path: '/:workspace_name/account/workspace'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/settings/developers'),
    path: '/:workspace_name/account/developers'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/settings/apps'),
    path: '/:workspace_name/account/apps'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/autopilot'),
    path: '/:workspace_name/account/autopilot'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/settings/logs'),
    path: '/:workspace_name/account/logs'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/settings/integrations'),
    path: '/:workspace_name/account/integrations'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/settings/members'),
    path: '/:workspace_name/account/members'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/settings/reports'),
    path: '/:workspace_name/account/reports'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/settings/notifications'),
    path: '/:workspace_name/account/notifications'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/settings/email-configuration'),
    path: '/:workspace_name/account/offers-email-configuration'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/app-download-configuration'),
    path: '/:workspace_name/account/app-download-configuration'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/voice-bot'),
    path: '/:workspace_name/account/voice-bot'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/voice-bot/reports'),
    path: '/:workspace_name/account/voice-bot-reports'
  },
  { getRedirectPath: () => '/login', path: '/login' },
  { getRedirectPath: () => '/onboarding', path: '/onboarding' },
  { getRedirectPath: () => '/onboarding/profile-picture', path: '/onboarding-profile-picture' },
  { getRedirectPath: () => '/verify', path: '/verify' },
  { getRedirectPath: () => '/login', path: '/verify-invitation' },
  { getRedirectPath: () => '/carrier-onboarding', path: '/carrier-onboarding' },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/public/posts'),
    path: '/:handle'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, `/public/posts/${params.post_id}`),
    path: '/:handle/posts/:post_id'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, '/public/posts'),
    path: '/:handle/search'
  },
  {
    getRedirectPath: ({ params, selectedWorkspaceName }) =>
      getWorkspacePath(params, selectedWorkspaceName, `/public/tracking/${params.shipmentID}`),
    path: '/:handle/share/shipment/tracking/:shipmentID'
  },
  {
    getRedirectPath: ({ params }) => `/public/log/${params.requestID}`,
    path: '/share/log/:requestID'
  },
  {
    getRedirectPath: ({ params }) => `/embed/chat/${params.shipmentID}`,
    path: '/embed/chat/:shipmentID'
  },
  {
    getRedirectPath: ({ params }) => `/embed/map/${params.shipmentID}`,
    path: '/embed/map/:shipmentID'
  }
]

export const isValidNewAppUrl = (newAppUrl: string | null | undefined) => {
  if (!newAppUrl) return false
  return !invalidRedirectValues.has(newAppUrl.trim().toLowerCase())
}

export const resolveNewAppRedirectPath = ({
  pathname,
  selectedWorkspaceName,
  newAppUrl
}: IResolveNewAppRedirectPathInput) => {
  if (!isValidNewAppUrl(newAppUrl)) return null

  const normalizedPathname = normalizePathname(pathname)

  for (const redirectRoute of redirectRoutes) {
    const matchedRoute = matchPath({ end: true, path: redirectRoute.path }, normalizedPathname)
    if (!matchedRoute) continue

    const resolvedPath = redirectRoute.getRedirectPath({
      params: matchedRoute.params,
      selectedWorkspaceName
    })

    return resolvedPath || '/login'
  }

  return '/login'
}

export const buildNewAppRedirectUrl = (newAppUrl: string | null | undefined, redirectPath: string) => {
  if (!isValidNewAppUrl(newAppUrl)) return null

  const normalizedBaseUrl = (newAppUrl || '').trim().replace(/\/+$/, '')
  const normalizedRedirectPath = redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`
  return `${normalizedBaseUrl}${normalizedRedirectPath}`
}

export const shouldForceRedirectToNewApp = ({ isWebsiteRoute, newAppUrl }: IShouldForceRedirectToNewAppInput) => {
  if (isWebsiteRoute) return false
  return isValidNewAppUrl(newAppUrl)
}
